import { WeeklyOpenSlotItem } from "@/pages/multishop-selection/responses";
import { useFeedback } from "@/providers/FeedbackProvider";
import { ShopRow } from "@/types/db";
import { copyToClipboard } from "@/utils/clipboard";
import { minuteOfDayToTime } from "@/utils/schedule";
import { useMemo } from "react";


export type DressTourTimelineSlot = {
  id: number | string;
  shopId: ShopRow["id"];
  shopName: ShopRow["shopName"];
  time?: string;
  startMinuteOfDay: WeeklyOpenSlotItem["startMinuteOfDay"];
  slotDate: WeeklyOpenSlotItem["slotDate"];
  startTimeCode: WeeklyOpenSlotItem["startTimeCode"];
  penaltyLink: ShopRow['penaltyLink'] | null;
  phoneNumber: ShopRow['phoneNumber'] | null;
  naverPlaceId?: ShopRow['naverPlaceId'] | number | null;
};

export type DressTourTimeLineProps = {
  hideOtherButton: boolean;
  slots: DressTourTimelineSlot[];
};

function parseTimeToMinuteOfDay(time?: string): number | null {
  if (!time) return null;

  const [hour, minute] = String(time).split(":").map(Number);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }

  return hour * 60 + minute;
}

function getSlotStartMinute(slot: DressTourTimelineSlot): number | null {
  if (
    slot.startMinuteOfDay !== undefined &&
    slot.startMinuteOfDay !== null &&
    Number.isFinite(slot.startMinuteOfDay)
  ) {
    return Number(slot.startMinuteOfDay);
  }

  return parseTimeToMinuteOfDay(slot.time);
}

function formatDurationLabel(totalMinutes: number): string {
  if (totalMinutes <= 0) {
    return "이동 0분";
  }

  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;

  if (hour > 0 && minute > 0) {
    return `이동 ${hour}시간 ${minute}분`;
  }

  if (hour > 0) {
    return `이동 ${hour}시간`;
  }

  return `이동 ${minute}분`;
}

function getTravelMinutes(
  currentSlot: DressTourTimelineSlot,
  nextSlot: DressTourTimelineSlot,
  visitMinutes = 60
): number | null {
  const currentStart = getSlotStartMinute(currentSlot);
  const nextStart = getSlotStartMinute(nextSlot);

  if (currentStart === null || nextStart === null) {
    return null;
  }

  return Math.max(0, nextStart - currentStart - visitMinutes);
}

function getSortValue(slot: DressTourTimelineSlot): number {
  const minute = getSlotStartMinute(slot);
  return minute === null ? Number.MAX_SAFE_INTEGER : minute;
}


export default function DressTourTimeLine({ slots, hideOtherButton = false }: DressTourTimeLineProps) {
  const { callFeedback } = useFeedback()
  const copyToClipboardForMap = async (naverPlaceId) => {
    await copyToClipboard(`https://map.naver.com/p/entry/place/${naverPlaceId}`)
    callFeedback('네이버 지도 주소가 복사되었습니다')
  }
  const sortedSlots = useMemo(() => {
    return [...slots].sort((a, b) => getSortValue(a) - getSortValue(b));
  }, [slots]);

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className="ri-route-line text-blue-600 text-sm"></i>
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            방문 일정 타임라인
          </span>
        </div>
        <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full">
          {slots.length}개 업체
        </span>
      </div>

      <div className="px-5 py-5">
        <div className="space-y-0">
          {sortedSlots.map((slot, index) => (
            <div key={`${slot.id ?? slot.shopName}_${index}`} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 pt-1">
                <span className="text-sm font-bold text-gray-900">{slot.time}</span>
              </div>

              <div className="flex flex-col items-center flex-shrink-0" style={{ paddingTop: 9 }}>
                <div className="w-3 h-3 rounded-full bg-blue-600 border-2 border-white ring-2 ring-blue-200 mt-0.5"></div>
                {index < slots.length - 1 && (
                  <div className="w-0.5 h-20 bg-gradient-to-b from-blue-300 to-blue-100 my-0.5"></div>
                )}
              </div>

              <div className={`flex-1 ${index < slots.length - 1 ? "pb-4" : ""}`}>
                <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{slot.shopName}</p>
                        <span className="flex-shrink-0 text-xs text-gray-400 font-normal">
                          방문 1시간
                        </span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-1">
                        <a
                          href={slot.penaltyLink}
                          target='_blank'
                          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors cursor-pointer group"
                        >
                          <i className="ri-file-text-line text-xs group-hover:text-blue-500"></i>
                          
                          <span className="underline underline-offset-2 decoration-dashed">
                            위약금 안내
                          </span>
                        </a>
                      </div>
                    </div>
                    {
                      !hideOtherButton && (

                        <div className='flex items-center gap-2 hidden md:flex'>
                          {
                            slot.naverPlaceId ? (
                              <a target='_blank' href={`https://map.naver.com/p/entry/place/${slot.naverPlaceId}`} className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-map-pin-2-line text-blue-500 text-sm"></i>
                              </a>
                            ) : <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 cursor-not-allowed" title='지도 정보가 없습니다'>
                                <i className="ri-map-pin-2-line text-gray-400 text-sm"></i>
                              </div>
                          }
                          {
                            slot.phoneNumber ? (
                              <a target='_blank' href={`tel:${slot.phoneNumber}`} className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-phone-line text-blue-500 text-sm"></i>
                              </a>
                            ) : <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 cursor-not-allowed" title='전화번호가 없습니다'>
                                <i className="ri-phone-line text-gray-500 text-sm"></i>
                              </div>
                          }
                          {
                            slot.naverPlaceId ? (
                              <a 
                                onClick={e => {
                                  e.preventDefault();
                                  copyToClipboardForMap(slot.naverPlaceId)
                                }}
                                className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0"
                              >
                                <i className="ri-share-line text-blue-500 text-sm"></i>
                              </a>
                            ) : (
                              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 cursor-not-allowed" title='전화번호가 없습니다'>
                                <i className="ri-share-line text-gray-500 text-sm"></i>
                              </div>
                            )
                          }
                          
                        </div>
                      )
                    }
                  </div>
                </div>

                {index < sortedSlots.length - 1 && (() => {
                  const nextSlot = sortedSlots[index + 1];
                  const travelMinutes = getTravelMinutes(slot, nextSlot, 60);

                  return (
                    <div className="flex items-center gap-2 mt-2 ml-1">
                      <i className="ri-arrow-down-line text-gray-300 text-xs"></i>
                      <span className="text-xs text-gray-400">
                        {travelMinutes === null ? "이동 시간 확인 필요" : formatDurationLabel(travelMinutes)}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}